// =============================================================================
// unleashedPushError.ts — turn a raw push error into a readable message
// =============================================================================
// The push worker stores whatever Unleashed returned in
// request_sales_order_pushes.error_message, which is usually the raw HTTP body,
// e.g.  HTTP 400: {"Items":[{"Field":"Product","Description":"… does not exist."}]}
// or    HTTP 403: (403) Forbidden
// This PURE helper parses that into a one-line, human-readable message (plus the
// structured items + HTTP code) so the Sales Orders screen can show something
// actionable instead of a JSON blob. Unit-tested; no imports.
// =============================================================================

export interface PushErrorItem {
  field: string | null;
  description: string;
}

export interface ParsedPushError {
  code: number | null; // the HTTP status, if the message carried one
  message: string; // friendly one-liner (may join several items with "; ")
  items: PushErrorItem[]; // structured field/description pairs, when present
  raw: string; // the original string, for a "show raw" affordance
}

// Parse a stored push error_message into a readable shape. Never throws.
export function formatUnleashedPushError(raw: string | null | undefined): ParsedPushError {
  const text = (raw ?? "").trim();
  if (!text) return { code: null, message: "", items: [], raw: "" };

  // Strip a leading "HTTP <code>: " prefix the worker prepends.
  const prefix = text.match(/^HTTP\s+(\d+):\s*/i);
  const code = prefix ? Number(prefix[1]) : null;
  const rest = (prefix ? text.slice(prefix[0].length) : text).trim();

  // Try to read a JSON payload out of the remainder.
  let json: unknown = null;
  try {
    json = JSON.parse(rest);
  } catch {
    json = null;
  }

  const obj = json && typeof json === "object" ? (json as Record<string, unknown>) : null;

  // Unleashed validation errors: { Items: [{ Field, Description }] }.
  if (obj && Array.isArray(obj.Items) && obj.Items.length > 0) {
    const items: PushErrorItem[] = obj.Items.map((it) => {
      const row = it && typeof it === "object" ? (it as Record<string, unknown>) : {};
      const field =
        typeof row.Field === "string" && row.Field.trim() !== "" ? row.Field.trim() : null;
      const description = typeof row.Description === "string" ? row.Description.trim() : "";
      return { field, description };
    });
    const message = items
      .map((it) => (it.field ? `${it.field}: ${it.description}` : it.description))
      .filter((s) => s !== "")
      .join("; ");
    return { code, message: message || rest, items, raw: text };
  }

  // Single-message shape: { Description: "…" } (e.g. the "could not be parsed" body).
  if (obj && typeof obj.Description === "string") {
    const description = obj.Description.trim();
    return { code, message: description, items: [{ field: null, description }], raw: text };
  }

  // Not JSON (e.g. "(403) Forbidden") — use the remainder as the message.
  return { code, message: rest || text, items: [], raw: text };
}
